import { wpQuery } from './wp'
import type { CasePageProps, CaseBlock } from '@pages/CasePage'

// ── Raw GraphQL shape ─────────────────────────────────────────────────────────

interface WpImageNode {
  node: { sourceUrl: string } | null
}

interface WpDirection {
  nodes: { name: string }[]
}

interface WpCaseData {
  name: string | null
  short_description: string | null
  cover: WpImageNode | null
  year: number | null
  // facts (up to 6 pairs)
  fact1Label: string | null; fact1Value: string | null
  fact2Label: string | null; fact2Value: string | null
  fact3Label: string | null; fact3Value: string | null
  fact4Label: string | null; fact4Value: string | null
  fact5Label: string | null; fact5Value: string | null
  fact6Label: string | null; fact6Value: string | null
}

interface WpCaseBlocks {
  // block 1 — task + works
  showTaskAndWorks: boolean | null
  taskText: string | null
  worksList: string | null
  // block 2 — task → execution
  showTaskExecution: boolean | null
  task: string | null
  execution: string | null
  // block 3 — route
  showRoute: boolean | null
  routeTitle: string | null
  routeSteps: string | null   // "title|desc|duration" per line
  // block 4 — photo stages: "url | title" per line
  showPhotoStages: boolean | null
  photoStagesTitle: string | null
  photoStagesList: string | null
  // block 5 — photos: "url | label" per line
  showPhotos: boolean | null
  photosList: string | null
}

interface WpCase {
  databaseId: number
  slug: string
  title: string
  directionsTax: WpDirection
  caseData: WpCaseData | null
  caseBlocks: WpCaseBlocks | null
}

interface WpCasesResponse {
  cases: { nodes: WpCase[] }
}

interface WpCaseBySlugResponse {
  case: WpCase | null
}

// ── GraphQL fragments ─────────────────────────────────────────────────────────

const IMG = `{ node { sourceUrl } }`

const CASE_DATA_FIELDS = /* graphql */ `
  caseData {
    name short_description year
    cover ${IMG}
    fact1Label fact1Value
    fact2Label fact2Value
    fact3Label fact3Value
    fact4Label fact4Value
    fact5Label fact5Value
    fact6Label fact6Value
  }
`

const CASE_BLOCKS_FIELDS = /* graphql */ `
  caseBlocks {
    showTaskAndWorks taskText worksList
    showTaskExecution task execution
    showRoute routeTitle routeSteps
    showPhotoStages photoStagesTitle photoStagesList
    showPhotos photosList
  }
`

const CASE_FIELDS = /* graphql */ `
  databaseId slug title
  directionsTax { nodes { name } }
  ${CASE_DATA_FIELDS}
  ${CASE_BLOCKS_FIELDS}
`

// ── Helpers ───────────────────────────────────────────────────────────────────

function imgUrl(img: WpImageNode | null | undefined): string | undefined {
  return img?.node?.sourceUrl ?? undefined
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}

// Парсит textarea "url | text" построчно, пропускает пустые строки
function parseUrlTextList(raw: string | null): { photo: string; label: string }[] {
  if (!raw) return []
  return raw.split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const sep = line.indexOf('|')
      if (sep === -1) return { photo: line.trim(), label: '' }
      return { photo: line.slice(0, sep).trim(), label: line.slice(sep + 1).trim() }
    })
    .filter(item => item.photo)
}

function buildFacts(d: WpCaseData | null): { k: string; v: string }[] {
  if (!d) return []
  return ([
    [d.fact1Label, d.fact1Value],
    [d.fact2Label, d.fact2Value],
    [d.fact3Label, d.fact3Value],
    [d.fact4Label, d.fact4Value],
    [d.fact5Label, d.fact5Value],
    [d.fact6Label, d.fact6Value],
  ] as [string | null, string | null][])
    .filter(([k, v]) => k && v)
    .map(([k, v]) => ({ k: k!, v: v! }))
}

function buildBlocks(b: WpCaseBlocks | null): CaseBlock[] {
  if (!b) return []
  const result: CaseBlock[] = []

  if (b.showTaskAndWorks) {
    result.push({
      type: 'task-and-works',
      paragraphs: (b.taskText ?? '').split('\n').filter(Boolean),
      items: (b.worksList ?? '').split('\n').filter(Boolean),
    })
  }

  if (b.showTaskExecution) {
    result.push({
      type: 'task-execution',
      task: b.task ?? '',
      execution: b.execution ?? '',
    })
  }

  if (b.showRoute) {
    const steps = (b.routeSteps ?? '')
      .split('\n')
      .filter(Boolean)
      .map(line => {
        const [title = '', desc = '', duration = ''] = line.split('|').map(s => s.trim())
        return { title, desc, duration }
      })
      .filter(s => s.title)
    if (steps.length) result.push({ type: 'route', title: b.routeTitle ?? undefined, steps })
  }

  if (b.showPhotoStages) {
    const steps = parseUrlTextList(b.photoStagesList)
      .map(({ photo, label }) => ({ title: label || photo, photo }))
    if (steps.length) result.push({ type: 'photo-stages', title: b.photoStagesTitle ?? undefined, steps })
  }

  if (b.showPhotos) {
    const items = parseUrlTextList(b.photosList)
    if (items.length) result.push({ type: 'photos', items })
  }

  return result
}

function wpCaseToCasePageProps(c: WpCase): CasePageProps {
  const d = c.caseData
  return {
    category: capitalize(c.directionsTax.nodes[0]?.name ?? ''),
    year: d?.year != null ? String(d.year) : '',
    title: d?.name ?? c.title,
    subtitle: d?.short_description ?? '',
    mainPhoto: imgUrl(d?.cover),
    facts: buildFacts(d),
    blocks: buildBlocks(c.caseBlocks),
    similar: [],
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function fetchCaseBySlug(slug: string): Promise<CasePageProps | null> {
  const data = await wpQuery<WpCaseBySlugResponse>(
    /* graphql */ `
    query CaseBySlug($slug: ID!) {
      case(id: $slug, idType: SLUG) { ${CASE_FIELDS} }
    }
  `,
    { slug },
  )
  if (!data.case) return null
  return wpCaseToCasePageProps(data.case)
}

export interface CaseListItem {
  slug: string
  title: string
  year: string
  category: string
  mainPhoto?: string
}

export async function fetchCaseList(first = 100, language: string = 'RU'): Promise<CaseListItem[]> {
  const data = await wpQuery<WpCasesResponse>(/* graphql */ `
    query CaseList($first: Int!, $language: LanguageCodeFilterEnum) {
      cases(first: $first, where: { language: $language }) {
        nodes {
          slug title
          directionsTax { nodes { name } }
          caseData { name year cover ${IMG} }
        }
      }
    }
  `, { first, language })
  return data.cases.nodes.map(c => ({
    slug: c.slug,
    title: c.caseData?.name ?? c.title,
    year: c.caseData?.year != null ? String(c.caseData.year) : '',
    category: capitalize(c.directionsTax.nodes[0]?.name ?? ''),
    mainPhoto: imgUrl(c.caseData?.cover),
  }))
}
